import React, { Suspense, lazy } from 'react';
import { ArrowLeft } from 'lucide-react';
import { FlowEditor } from '@/components/flow';
import { ImageCropper } from '@/components/ImageCropper';
import { SmartSequenceDock } from '@/components/SmartSequenceDock';
import { SonicStudio } from '@/components/SonicStudio';
import { SettingsModal } from '@/components/SettingsModal';
import { SidebarDock } from '@/components/SidebarDock';
import { WelcomeScreen } from '@/components/WelcomeScreen';
import { FlowingSphere } from '@/components/FlowingSphere';
import { useNodeStore } from '@/stores/nodeStore';
import { useTaskStore } from '@/stores/taskStore';
import { useModelStore } from '@/stores/modelStore';
import { useDataPersistence } from '@/hooks/useDataPersistence';
import { useHistory } from '@/hooks/useHistory';
import { useNodeOperations } from '@/hooks/useNodeOperations';
import { useGenerationOrchestrator } from '@/hooks/useGenerationOrchestrator';
import { usePanoramaGenerator } from '@/hooks/usePanoramaGenerator';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useTaskRecovery } from '@/hooks/useTaskRecovery';
import { NodeType, NodeStatus, SmartSequenceItem, AppNode } from '@/types';
import { generateApi, GenerateRequest } from '@/services/generateApi';
import { toast } from 'sonner';

import { AssistantPanel } from '@/components/agent/AssistantPanel';

// SketchEditor 体积大且按需使用，保留 lazy import
const SketchEditor = React.lazy(() => import('@/components/SketchEditor').then(m => ({ default: m.SketchEditor })));

const SPRING = "cubic-bezier(0.32, 0.72, 0, 1)";

interface EditorLayoutProps {
  isTransitioning: boolean;
  transitionDirection: string | null;
  onBackToProjects: () => void;
  workflows: any[];
  selectedWorkflowId: string | null;
  onSelectWorkflow: (id: string) => void;
  onSaveWorkflow: () => void;
  onDeleteWorkflow: (id: string) => void;
  onRenameWorkflow: (id: string, title: string) => void;
  onOpenSettings: () => void;
  isSettingsOpen: boolean;
  onCloseSettings: () => void;
  isChatOpen: boolean;
  onToggleChat: () => void;
  isMultiFrameOpen: boolean;
  onToggleMultiFrame: () => void;
  isSonicStudioOpen: boolean;
  onToggleSonicStudio: () => void;
  assetHistory: any[];
  setAssetHistory: React.Dispatch<React.SetStateAction<any[]>>;
  sphereFlowing: boolean;
}

export const EditorLayout: React.FC<EditorLayoutProps> = ({
  isTransitioning,
  transitionDirection,
  onBackToProjects,
  workflows,
  selectedWorkflowId,
  onSelectWorkflow,
  onSaveWorkflow,
  onDeleteWorkflow,
  onRenameWorkflow,
  onOpenSettings,
  isSettingsOpen,
  onCloseSettings,
  isChatOpen,
  onToggleChat,
  isMultiFrameOpen,
  onToggleMultiFrame,
  isSonicStudioOpen,
  onToggleSonicStudio,
  assetHistory,
  setAssetHistory,
  sphereFlowing,
}) => {
  const nodes = useNodeStore((s: any) => s.nodes);
  const loadModels = useModelStore((s: any) => s.loadModels);
  const loadFromLocalStorage = useTaskStore((s: any) => s.loadFromLocalStorage);
  const connectWebSocket = useTaskStore((s: any) => s.connectWebSocket);
  const disconnectWebSocket = useTaskStore((s: any) => s.disconnectWebSocket);

  const [clipboard, setClipboard] = React.useState<any>(null);
  const [croppingNodeId, setCroppingNodeId] = React.useState<string | null>(null);
  const [imageToCrop, setImageToCrop] = React.useState<string | null>(null);
  const [isSketchEditorOpen, setIsSketchEditorOpen] = React.useState(false);

  const { saveHistory, undo } = useHistory();
  const {
    handleAddNode, handleNodeUpdate, handleNodeDelete,
    handleExtractImage, handleAssetGenerated, assetHistory: opAssetHistory,
  } = useNodeOperations(saveHistory);
  const { handleNodeAction } = useGenerationOrchestrator(handleNodeUpdate, handleAssetGenerated);
  const { handleGeneratePanorama } = usePanoramaGenerator(saveHistory, handleAssetGenerated);

  useTaskRecovery({ isLoaded: true, handleAssetGenerated });

  const onAddNode = React.useCallback((type: NodeType, position?: { x: number; y: number }, initialData?: any) => {
    const result = handleAddNode(type, position, initialData);
    if (result === 'open_sketch_editor') setIsSketchEditorOpen(true);
    return result;
  }, [handleAddNode]);

  useKeyboardShortcuts({ undo, saveHistory, clipboard, setClipboard, addNode: (node: AppNode) => { useNodeStore.getState().addNode(node); } });

  const handleSketchResult = (type: 'image' | 'video', result: string, prompt: string) => {
    handleAddNode(NodeType.IMAGE_GENERATOR, { x: 100, y: 100 }, { image: result, prompt, status: NodeStatus.SUCCESS });
    handleAssetGenerated(type, result, prompt || 'Sketch Output');
  };

  const handleMultiFrameGenerate = async (frames: SmartSequenceItem[]): Promise<string> => {
    const complexPrompt = "A sequence showing: " + frames.map(f => f.transition?.prompt || "scene").join(" transitioning to ");
    try {
      const request: GenerateRequest = {
        modelId: 'veo-3.1-generate-preview',
        prompt: complexPrompt,
        mode: frames[0].src ? 'image-to-video' : 'text-to-video',
        inputImages: frames[0].src ? [frames[0].src] : undefined,
        params: { aspectRatio: '16:9', referenceImages: frames.length > 1 ? frames.map(f => f.src) : undefined },
      };
      const res = await generateApi.generateSync(request);
      const uri = res.results?.[0]?.url;
      if (!uri) throw new Error('Smart Sequence Generation Failed');
      const isFallback = res.results?.[0]?.outputType === 'image';
      if (isFallback) handleAssetGenerated('image', uri, 'Smart Sequence Preview (Fallback)');
      else handleAssetGenerated('video', uri, 'Smart Sequence');
      return uri;
    } catch (e: any) {
      throw new Error(e.message || 'Smart Sequence Generation Failed');
    }
  };

  return (
    <div className={`w-full h-full overflow-hidden text-slate-200 selection:bg-cyan-500/30 transition-all duration-700 ease-[${SPRING}] ${isTransitioning && transitionDirection === 'toProjects' ? 'opacity-0 scale-105' : 'opacity-100 scale-100 animate-in fade-in slide-in-from-bottom-4'}`}>
      <div className="absolute inset-0 noise-bg" />
      <button onClick={onBackToProjects} className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2.5 bg-zinc-900/80 hover:bg-zinc-800/80 border border-zinc-700/50 hover:border-zinc-600/50 rounded-xl text-zinc-400 hover:text-zinc-200 transition-all z-[60] backdrop-blur-xl shadow-lg">
        <ArrowLeft size={16} />
        <span className="text-sm font-medium">返回项目</span>
      </button>

      <WelcomeScreen onAddNode={onAddNode} visible={nodes.length === 0} />

      <FlowEditor
        onNodeAction={handleNodeAction}
        onNodeUpdate={handleNodeUpdate}
        onNodeDelete={handleNodeDelete}
        onAddNode={onAddNode}
        onExtractImage={handleExtractImage}
        onGeneratePanorama={handleGeneratePanorama}
        onToggleChat={onToggleChat}
        onOpenChat={() => { if (!isChatOpen) onToggleChat(); }}
      />
      {croppingNodeId && imageToCrop && <ImageCropper imageSrc={imageToCrop} onCancel={() => { setCroppingNodeId(null); setImageToCrop(null); }} onConfirm={(b: any) => { handleNodeUpdate(croppingNodeId, { croppedFrame: b }); setCroppingNodeId(null); setImageToCrop(null); }} />}
      {isSketchEditorOpen && <Suspense fallback={null}><SketchEditor onClose={() => setIsSketchEditorOpen(false)} onGenerate={handleSketchResult} /></Suspense>}
      <SmartSequenceDock isOpen={isMultiFrameOpen} onClose={() => onToggleMultiFrame()} onGenerate={handleMultiFrameGenerate} onConnectStart={(e: any) => { e.preventDefault(); e.stopPropagation(); }} />
      <SonicStudio isOpen={isSonicStudioOpen} onClose={() => onToggleSonicStudio()} history={assetHistory.filter((a: any) => a.type === 'audio')} onGenerate={(src: any, prompt: any) => handleAssetGenerated('audio', src, prompt)} />
      <SettingsModal isOpen={isSettingsOpen} onClose={onCloseSettings} />
      <SidebarDock
        onAddNode={onAddNode}
        onUndo={undo}
        isChatOpen={isChatOpen}
        onToggleChat={onToggleChat}
        isMultiFrameOpen={isMultiFrameOpen}
        onToggleMultiFrame={onToggleMultiFrame}
        isSonicStudioOpen={isSonicStudioOpen}
        onToggleSonicStudio={onToggleSonicStudio}
        assetHistory={assetHistory}
        onHistoryItemClick={(item: any) => { const data = item.type.includes('image') ? { image: item.src } : { videoUri: item.src }; onAddNode(NodeType.MEDIA_UPLOAD, undefined, data); }}
        onDeleteAsset={(id: any) => setAssetHistory(prev => prev.filter(a => a.id !== id))}
        workflows={workflows}
        selectedWorkflowId={selectedWorkflowId}
        onSelectWorkflow={(id: any) => { if (id) onSelectWorkflow(id); }}
        onSaveWorkflow={onSaveWorkflow}
        onDeleteWorkflow={(id: any) => { onDeleteWorkflow(id); }}
        onRenameWorkflow={onRenameWorkflow}
        onOpenSettings={onOpenSettings}
      />
      <Suspense fallback={<div className="w-[420px] h-full bg-[#1c1c1e] animate-pulse" />}>
        <AssistantPanel isOpen={isChatOpen} onClose={() => onToggleChat()} />
      </Suspense>

      {!isChatOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-2 group">
          <span className="px-3 py-1.5 text-xs font-medium text-white/90 bg-[#1c1c1e]/90 backdrop-blur-xl border border-white/10 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
            Bong·ai Agent
          </span>
          <button
            onClick={() => onToggleChat()}
            className="flex items-center justify-center rounded-full hover:scale-110 transition-transform duration-300 cursor-pointer"
            title="打开智能体"
          >
            <FlowingSphere size={40} isFlowing={sphereFlowing} />
          </button>
        </div>
      )}
    </div>
  );
};
